import { useCallback, useEffect, useRef, useState } from 'react'
import { VerificationDocs } from './VerificationDocs'
import { useNavigate } from 'react-router-dom'
import { meApi, type Profile, type ProfileContacts, type ProfileUpdate } from '../../api/endpoints'
import { useIdentity } from '../../lib/identity'

/**
 * ProfilePanel — the account's editable source of truth, shell-agnostic.
 *
 * Signup collects company name, contact person, address, website and phone(s)
 * and then never surfaces them again — a supplier with a wrong number had no
 * way to fix it. This is that page. ONE component; the desktop route
 * (routes/AccountProfile.tsx) and the mobile screen
 * (mobile/screens/account/ProfileAccountScreen.tsx) both render it so the two
 * shells cannot drift.
 *
 * ── DIRECTORY-APPROPRIATE ──────────────────────────────────────────────────
 * v0 DiaEx is a free directory. This page is company identity + the public
 * contact card, NOT billing / concierge / Aska+ (those live on their own
 * screens). Sections: Company, Address, Contacts-by-country (the multi-country
 * phone book with ONE business-wide WhatsApp designate), Contact person, and —
 * suppliers only — the declared return policy.
 *
 * ── THE CONTACTS EDITOR IS THE INTERESTING PART ────────────────────────────
 * A supplier may operate in several countries. Per country: add numbers and
 * optionally mark one primary. Across the WHOLE business exactly ONE number is
 * the WhatsApp contact (a single radio group spanning every country — Tej
 * 2026-09-21, "only one whatsapp number per business"). The server
 * (shared/contacts.ts) is the invariant gate — the UI just makes the choices
 * obvious and sends a clean payload.
 */

type Phase = 'loading' | 'ready' | 'signed_out' | 'error'

// Trade centers first, then A–Z — the SAME list as the signup AddressStep, so a
// buyer sees the same country names in both places.
const COUNTRIES: Array<[string, string]> = [
  ['US', 'United States'], ['IN', 'India'], ['AE', 'United Arab Emirates'],
  ['BE', 'Belgium'], ['IL', 'Israel'], ['HK', 'Hong Kong'], ['GB', 'United Kingdom'],
  ['SG', 'Singapore'], ['TH', 'Thailand'], ['JP', 'Japan'], ['AU', 'Australia'], ['ZA', 'South Africa'],
  ['AR', 'Argentina'], ['AT', 'Austria'], ['BD', 'Bangladesh'], ['BR', 'Brazil'], ['BW', 'Botswana'],
  ['CA', 'Canada'], ['CH', 'Switzerland'], ['CL', 'Chile'], ['CN', 'China'], ['CO', 'Colombia'],
  ['CZ', 'Czechia'], ['DE', 'Germany'], ['DK', 'Denmark'], ['EG', 'Egypt'], ['ES', 'Spain'],
  ['FI', 'Finland'], ['FR', 'France'], ['GR', 'Greece'], ['HU', 'Hungary'], ['ID', 'Indonesia'],
  ['IE', 'Ireland'], ['IT', 'Italy'], ['JO', 'Jordan'], ['KE', 'Kenya'], ['KR', 'South Korea'],
  ['KW', 'Kuwait'], ['LB', 'Lebanon'], ['LK', 'Sri Lanka'], ['LU', 'Luxembourg'], ['MX', 'Mexico'],
  ['MY', 'Malaysia'], ['NA', 'Namibia'], ['NL', 'Netherlands'], ['NO', 'Norway'], ['NZ', 'New Zealand'],
  ['OM', 'Oman'], ['PA', 'Panama'], ['PE', 'Peru'], ['PH', 'Philippines'], ['PK', 'Pakistan'],
  ['PL', 'Poland'], ['PT', 'Portugal'], ['QA', 'Qatar'], ['RO', 'Romania'], ['SA', 'Saudi Arabia'],
  ['SE', 'Sweden'], ['TR', 'Türkiye'], ['TW', 'Taiwan'], ['UA', 'Ukraine'], ['UY', 'Uruguay'], ['VN', 'Vietnam'],
]
const countryName = (code: string) => COUNTRIES.find(([c]) => c === code)?.[1] ?? code

/** Local editing shape — mirrors ProfileContacts but every phone gets a stable
 *  key so React lists don't thrash while typing. */
interface DraftPhone { key: string; number: string; isWhatsApp: boolean; isPrimary: boolean; label: string }
interface DraftCountry { countryCode: string; phones: DraftPhone[] }

let keySeq = 0
const nextKey = () => `p${keySeq++}`

function toDraft(contacts: ProfileContacts): DraftCountry[] {
  return contacts.countries.map((c) => ({
    countryCode: c.countryCode,
    phones: c.phones.map((p) => ({
      key: nextKey(),
      number: p.number,
      isWhatsApp: !!p.isWhatsApp,
      isPrimary: !!p.isPrimary,
      label: p.label ?? '',
    })),
  }))
}

/** Draft → wire. Drops blank numbers / empty countries; the server re-asserts
 *  the invariants (one WhatsApp per country, at most one primary). */
function toWire(draft: DraftCountry[]): ProfileContacts {
  return {
    countries: draft
      .map((c) => ({
        countryCode: c.countryCode,
        phones: c.phones
          .filter((p) => p.number.trim())
          .map((p) => ({
            number: p.number.trim(),
            isWhatsApp: p.isWhatsApp,
            isPrimary: p.isPrimary,
            ...(p.label.trim() ? { label: p.label.trim() } : {}),
          })),
      }))
      .filter((c) => c.phones.length > 0),
  }
}

export function ProfilePanel({ onBack }: { onBack?: () => void }) {
  const navigate = useNavigate()
  const identity = useIdentity()
  const [phase, setPhase] = useState<Phase>('loading')
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  // Editable draft.
  const [companyName, setCompanyName] = useState('')
  const [contactName, setContactName] = useState('')
  const [website, setWebsite] = useState('')
  // The PERSONAL / agent-facing number — editable here ONLY while unverified.
  // An OTP-verified number is authoritative + read-only (server ignores writes
  // to it), so we hydrate from `profile.phone` but gate editing on
  // `!phoneVerified`. Distinct from the business WhatsApp in the contacts block.
  const [personalPhone, setPersonalPhone] = useState('')
  const [addr, setAddr] = useState({ line1: '', line2: '', city: '', state: '', postalCode: '', country: '' })
  const [countries, setCountries] = useState<DraftCountry[]>([])
  const [returnsAccepting, setReturnsAccepting] = useState(false)
  const [returnsDays, setReturnsDays] = useState('')
  const [returnsNote, setReturnsNote] = useState('')

  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const hydrate = useCallback((p: Profile) => {
    setProfile(p)
    setCompanyName(p.companyName ?? '')
    setContactName(p.contactName ?? '')
    setWebsite(p.website ?? '')
    // Seed the editable personal-number field. When verified, `phone` is the
    // OTP-attested number and the field renders read-only (see below).
    setPersonalPhone(p.phone ?? '')
    setAddr({
      line1: p.address?.line1 ?? '', line2: p.address?.line2 ?? '',
      city: p.address?.city ?? '', state: p.address?.state ?? '',
      postalCode: p.address?.postalCode ?? '', country: p.address?.country ?? '',
    })
    setCountries(toDraft(p.contacts))
    setReturnsAccepting(p.returns?.accepting ?? false)
    setReturnsDays(p.returns?.windowDays != null ? String(p.returns.windowDays) : '')
    setReturnsNote(p.returns?.note ?? '')
  }, [])

  const load = useCallback(async () => {
    setPhase('loading'); setLoadError(null)
    try {
      const { profile: p } = await meApi.profile()
      hydrate(p)
      setPhase('ready')
    } catch (err) {
      // A 401 before the token attaches is not an error page — same rule the org
      // panel uses; treat it as signed-out and offer sign-in.
      const status = (err as { status?: number })?.status
      if (status === 401) { setPhase('signed_out'); return }
      setLoadError('Couldn’t load your profile. Try again.')
      setPhase('error')
    }
  }, [hydrate])

  useEffect(() => {
    if (identity.status === 'loading') return
    void load()
  }, [load, identity.status])

  useEffect(() => () => { if (savedTimer.current) clearTimeout(savedTimer.current) }, [])

  const isAdmin = profile?.role === 'admin'
  const isSupplier = profile?.role === 'supplier' || profile?.role === 'admin' || profile?.accountType === 'supplier'
  // A buyer-only account (buys, never lists) has no buyer-facing contact card to
  // maintain — the number that matters is the PERSONAL one they use to reach our
  // agent. So the business-contact section is deprioritised for them.
  const buyerOnly = !isSupplier

  // Is this person's personal number OTP-verified? If so it's authoritative and
  // read-only (it changes only through the verification flow); otherwise the
  // field below is editable. The server ALREADY separates the personal number
  // from the business-WhatsApp mirror (`/me/profile` reads a dedicated
  // `personalPhone` field, never the overloaded flat `phone`), so `profile.phone`
  // here is genuinely this individual's number, not the business line.
  const phoneVerified = profile?.phoneVerified === true

  // Whether a business WhatsApp is currently designated anywhere.
  const hasBusinessWhatsApp = countries.some((c) => c.phones.some((p) => p.isWhatsApp && p.number.trim()))

  // ── Contacts editor mutations ──────────────────────────────────────────
  function addCountry(code: string) {
    if (!code) return
    if (countries.some((c) => c.countryCode === code)) return
    setCountries((prev) => [...prev, {
      countryCode: code,
      // The first number of the FIRST country auto-becomes the business
      // WhatsApp + that country's primary. Later countries don't steal the
      // designate — one WhatsApp per business (2026-09-21; server enforces).
      phones: [{
        key: nextKey(), number: '',
        isWhatsApp: !prev.some((c) => c.phones.some((p) => p.isWhatsApp)),
        isPrimary: true, label: '',
      }],
    }])
  }
  function removeCountry(code: string) {
    setCountries((prev) => prev.filter((c) => c.countryCode !== code))
  }
  function addPhone(code: string) {
    setCountries((prev) => prev.map((c) => c.countryCode === code
      ? { ...c, phones: [...c.phones, { key: nextKey(), number: '', isWhatsApp: false, isPrimary: false, label: '' }] }
      : c))
  }
  function removePhone(code: string, key: string) {
    setCountries((prev) => {
      const next = prev.map((c) => c.countryCode === code
        ? { ...c, phones: c.phones.filter((p) => p.key !== key) }
        : c)
      // Never leave the BUSINESS with no WhatsApp designate: if the removed
      // phone held it, the first remaining phone anywhere inherits it.
      if (!next.some((c) => c.phones.some((p) => p.isWhatsApp))) {
        const first = next.find((c) => c.phones.length > 0)
        if (first) {
          return next.map((c) => c === first
            ? { ...c, phones: c.phones.map((p, i) => (i === 0 ? { ...p, isWhatsApp: true } : p)) }
            : c)
        }
      }
      return next
    })
  }
  function patchPhone(code: string, key: string, patch: Partial<DraftPhone>) {
    setCountries((prev) => prev.map((c) => c.countryCode === code
      ? { ...c, phones: c.phones.map((p) => p.key === key ? { ...p, ...patch } : p) }
      : c))
  }
  function setWhatsApp(code: string, key: string) {
    // ONE WhatsApp per business: designating a number clears every other
    // number's flag in every country (2026-09-21).
    setCountries((prev) => prev.map((c) => ({
      ...c,
      phones: c.phones.map((p) => ({ ...p, isWhatsApp: c.countryCode === code && p.key === key })),
    })))
  }
  function togglePrimary(code: string, key: string) {
    setCountries((prev) => prev.map((c) => c.countryCode === code
      ? { ...c, phones: c.phones.map((p) => ({ ...p, isPrimary: p.key === key ? !p.isPrimary : false })) }
      : c))
  }

  // A country with no non-blank number gets silently DROPPED by toWire() — the
  // user adds "India", forgets to type a number, hits Save, sees "Saved", and
  // India is gone with no explanation. Catch it before the save and point at the
  // offending countries by name.
  const emptyCountryCodes = countries
    .filter((c) => !c.phones.some((p) => p.number.trim()))
    .map((c) => c.countryCode)

  async function save() {
    if (emptyCountryCodes.length) {
      setSaveError(
        `Add a number for ${emptyCountryCodes.map(countryName).join(', ')} — or remove ${emptyCountryCodes.length === 1 ? 'it' : 'them'}. A country with no number can’t be saved.`,
      )
      setSaved(false)
      return
    }
    setSaving(true); setSaveError(null); setSaved(false)
    try {
      const body: ProfileUpdate = {
        companyName: companyName.trim() || null,
        contactName: contactName.trim() || null,
        website: website.trim() || null,
        contacts: toWire(countries),
      }
      // The personal number — sent ONLY when it's editable (unverified). A
      // verified number is OTP-locked; the server ignores a write to it, but we
      // don't send it at all rather than round-trip a no-op. `personalPhone`
      // isn't on the ProfileUpdate type yet (endpoints.ts) — cast the one field.
      if (!phoneVerified) {
        (body as ProfileUpdate & { personalPhone?: string | null }).personalPhone =
          personalPhone.trim() || null
      }
      const anyAddr = addr.line1.trim() && addr.city.trim() && addr.country
      body.address = anyAddr
        ? {
            line1: addr.line1.trim(),
            line2: addr.line2.trim() || undefined,
            city: addr.city.trim(),
            state: addr.state.trim() || undefined,
            postalCode: addr.postalCode.trim() || undefined,
            country: addr.country,
            countryName: countryName(addr.country),
          }
        : null
      if (isSupplier) {
        const days = returnsDays.trim() ? Number(returnsDays) : null
        body.returns = {
          accepting: returnsAccepting,
          windowDays: Number.isFinite(days) && days ? Math.min(90, Math.max(1, days as number)) : null,
          note: returnsNote.trim() || null,
        }
      }
      const { profile: p } = await meApi.updateProfile(body)
      hydrate(p)
      setSaved(true)
      if (savedTimer.current) clearTimeout(savedTimer.current)
      savedTimer.current = setTimeout(() => setSaved(false), 3000)
    } catch (err) {
      const msg = (err as { body?: { error?: string } })?.body?.error
      setSaveError(msg || 'Couldn’t save those changes. Try again.')
    } finally {
      setSaving(false)
    }
  }

  const unusedCountries = COUNTRIES.filter(([c]) => !countries.some((x) => x.countryCode === c))

  if (phase === 'loading') {
    return (
      <div className="mx-auto w-full max-w-[720px] px-4 py-6 md:px-6" data-testid="profile-panel">
        <div className="skeleton h-8 w-48 rounded" />
        <div className="mt-6 space-y-3">
          <div className="skeleton h-12 rounded-md" />
          <div className="skeleton h-12 rounded-md" />
          <div className="skeleton h-12 rounded-md" />
        </div>
      </div>
    )
  }

  if (phase === 'signed_out') {
    return (
      <div className="mx-auto w-full max-w-[720px] px-4 py-10 text-center md:px-6" data-testid="profile-signed-out">
        <h1 className="font-serif text-2xl text-ink">Sign in to edit your profile</h1>
        <p className="mt-2 text-sm text-soft">Your company details and contacts live behind your account.</p>
        <button type="button" onClick={() => navigate('/signin')} className="btn-primary mt-6 h-11 px-6">Sign in</button>
      </div>
    )
  }

  if (phase === 'error') {
    return (
      <div className="mx-auto w-full max-w-[720px] px-4 py-10 text-center md:px-6" data-testid="profile-load-error">
        <h1 className="font-serif text-2xl text-ink">Couldn’t load your profile</h1>
        <p className="mt-2 text-sm text-soft">{loadError}</p>
        <button type="button" onClick={() => void load()} className="btn-primary mt-6 h-11 px-6">Try again</button>
      </div>
    )
  }

  return (
    <form
      className="mx-auto w-full max-w-[720px] px-4 py-6 md:px-6"
      onSubmit={(e) => { e.preventDefault(); void save() }}
      data-testid="profile-panel"
    >
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-2xl tracking-h1 text-ink md:text-3xl">Profile</h1>
          <p className="mt-1 text-sm text-soft">
            {profile?.email ?? ''} — your company details and how buyers reach you.
          </p>
        </div>
        {onBack && (
          <button type="button" onClick={onBack} className="btn-ghost h-9 shrink-0" data-testid="profile-back">Back</button>
        )}
      </header>

      <div className="rule-strong mt-5" />

      {/* ── Company ─────────────────────────────────────────────────────── */}
      <Section title="Company">
        <Field label="Company name">
          <input className="input h-11 w-full text-[16px]" value={companyName}
            onChange={(e) => setCompanyName(e.target.value)} data-testid="profile-company-name" />
        </Field>
        <Field label="Website" hint="A plain domain is fine — no https:// needed.">
          <input className="input h-11 w-full text-[16px]" inputMode="url" value={website}
            onChange={(e) => setWebsite(e.target.value)} data-testid="profile-website" />
        </Field>
      </Section>

      {/* ── Contact person ──────────────────────────────────────────────── */}
      <Section title="Contact person">
        <Field label="Who buyers ask for">
          <input className="input h-11 w-full text-[16px]" value={contactName}
            onChange={(e) => setContactName(e.target.value)} data-testid="profile-contact-name" />
        </Field>
      </Section>

      {/* ── Address ─────────────────────────────────────────────────────── */}
      <Section title="Address" hint="Your country sets duties and shipping.">
        <div className="grid gap-3 sm:grid-cols-2">
          <input className="input h-11 text-[16px] sm:col-span-2" placeholder="Street address" autoComplete="address-line1"
            value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} data-testid="profile-addr-line1" />
          <input className="input h-11 text-[16px] sm:col-span-2" placeholder="Suite, floor, building (optional)" autoComplete="address-line2"
            value={addr.line2} onChange={(e) => setAddr({ ...addr, line2: e.target.value })} />
          <input className="input h-11 text-[16px]" placeholder="City" autoComplete="address-level2"
            value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} data-testid="profile-addr-city" />
          <input className="input h-11 text-[16px]" placeholder="State / province" autoComplete="address-level1"
            value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })} />
          <input className="input h-11 text-[16px]" placeholder="Postal code" autoComplete="postal-code"
            value={addr.postalCode} onChange={(e) => setAddr({ ...addr, postalCode: e.target.value })} />
          <select className="input h-11 text-[16px]" aria-label="Country" value={addr.country}
            onChange={(e) => setAddr({ ...addr, country: e.target.value })} data-testid="profile-addr-country">
            <option value="" disabled>Country</option>
            {COUNTRIES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
          </select>
        </div>
      </Section>

      {/* ── Your personal number (agent-facing) ─────────────────────────────
          The number YOU use to message the DiaEx agent on WhatsApp — per
          person, distinct from the buyer-facing business numbers below. For a
          buyer-only account this is the number that matters, so it leads.
          Editable while unverified; read-only once OTP-verified. */}
      <PersonalNumberSection
        value={personalPhone}
        verified={phoneVerified}
        onChange={setPersonalPhone}
      />

      {/* ── Business contact numbers (buyer-facing) ─────────────────────────
          How BUYERS reach your company. One number is the business WhatsApp
          designate; per-country numbers stay for calls. Deprioritised (and
          retitled) for a buyer-only account, who has no card for buyers. */}
      <Section
        title={buyerOnly ? 'Business contact numbers (optional)' : 'Business contact numbers'}
        hint={buyerOnly
          ? 'Only needed if buyers or suppliers should be able to reach a company line. You don’t need this to browse or to message our agent.'
          : 'How buyers reach your company. Pick ONE number as your business WhatsApp — the one buyers message, wherever they are. Set a primary per country for your main office line.'}
      >
        <div className="space-y-4" data-testid="profile-contacts">
          {countries.length === 0 && (
            <p className="text-[13px] text-soft" data-testid="profile-contacts-empty">
              No phone numbers yet. Add a country below to get started.
            </p>
          )}

          {/* Which number is the business WhatsApp, spelled out — and how to
              move it, since a native radio can be set but never un-set. */}
          {countries.length > 0 && !buyerOnly && (
            <p className="text-[12.5px] text-soft" data-testid="profile-whatsapp-help">
              {hasBusinessWhatsApp
                ? 'The number marked “current” is your business WhatsApp. Tap another number’s Business WhatsApp button to move it — there’s always exactly one.'
                : 'No business WhatsApp set yet. Tap the Business WhatsApp button on the number buyers should message.'}
            </p>
          )}

          {countries.map((c) => (
            <CountryBlock
              key={c.countryCode}
              country={c}
              empty={emptyCountryCodes.includes(c.countryCode)}
              onAddPhone={() => addPhone(c.countryCode)}
              onRemoveCountry={() => removeCountry(c.countryCode)}
              onRemovePhone={(key) => removePhone(c.countryCode, key)}
              onPatchPhone={(key, patch) => patchPhone(c.countryCode, key, patch)}
              onSetWhatsApp={(key) => setWhatsApp(c.countryCode, key)}
              onTogglePrimary={(key) => togglePrimary(c.countryCode, key)}
            />
          ))}

          {unusedCountries.length > 0 && (
            <div className="flex items-center gap-2">
              <select
                className="input h-11 text-[16px] max-w-[240px]"
                aria-label="Add a country"
                data-testid="profile-add-country"
                value=""
                onChange={(e) => { addCountry(e.target.value); e.currentTarget.value = '' }}
              >
                <option value="" disabled>+ Add a country…</option>
                {unusedCountries.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
              </select>
            </div>
          )}
        </div>
      </Section>

      {/* ── Returns (supplier only) ─────────────────────────────────────── */}
      {isSupplier && (
        <Section title="Return policy" hint="Your own terms for DIRECT deals — shown on your contact card.">
          <label className="flex items-center gap-2 text-[14px] text-ink">
            <input type="checkbox" checked={returnsAccepting} onChange={(e) => setReturnsAccepting(e.target.checked)}
              className="h-4 w-4 accent-gold" data-testid="profile-returns-accepting" />
            I accept returns on direct deals
          </label>
          {returnsAccepting && (
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Field label="Return window (days)">
                <input className="input h-11 w-full text-[16px]" inputMode="numeric" value={returnsDays}
                  onChange={(e) => setReturnsDays(e.target.value.replace(/[^0-9]/g, ''))} data-testid="profile-returns-days" />
              </Field>
              <Field label="Note (optional)">
                <input className="input h-11 w-full text-[16px]" maxLength={140} value={returnsNote}
                  onChange={(e) => setReturnsNote(e.target.value)} data-testid="profile-returns-note" />
              </Field>
            </div>
          )}
        </Section>
      )}

      {saveError && (
        <p className="mt-4 text-[13px] font-medium text-danger" role="alert" data-testid="profile-save-error">{saveError}</p>
      )}
      {saved && !saveError && (
        <p className="mt-4 text-[13px] font-medium text-gold" role="status" data-testid="profile-saved">Saved.</p>
      )}

      <div className="mt-6 flex flex-wrap gap-2">
        <button type="submit" disabled={saving} className="btn-primary h-11 px-6" data-testid="profile-save">
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>

      {/* Seat documents (Tej 2026-09-20) — outside the form's save cycle:
          uploads land immediately via the signed-URL flow, no Save needed.
          NOT for admins: staff accounts don't submit KYC/identity docs, so an
          admin must never see "Identity document NEEDED" (Tej 2026-09-21). */}
      {!isAdmin && (
        <div className="pb-4">
          <VerificationDocs />
        </div>
      )}
    </form>
  )
}

function CountryBlock({
  country, empty, onAddPhone, onRemoveCountry, onRemovePhone, onPatchPhone, onSetWhatsApp, onTogglePrimary,
}: {
  country: DraftCountry
  /** True when this country has no non-blank number — it will be discarded on save. */
  empty: boolean
  onAddPhone: () => void
  onRemoveCountry: () => void
  onRemovePhone: (key: string) => void
  onPatchPhone: (key: string, patch: Partial<DraftPhone>) => void
  onSetWhatsApp: (key: string) => void
  onTogglePrimary: (key: string) => void
}) {
  return (
    <div className="rounded-md border border-line bg-paper p-3" data-testid={`profile-country-${country.countryCode}`}>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-[14px] font-semibold text-ink">{countryName(country.countryCode)}</h3>
        <button type="button" onClick={onRemoveCountry}
          className="text-[12px] text-soft hover:text-danger" data-testid={`profile-remove-country-${country.countryCode}`}>
          Remove
        </button>
      </div>
      {empty && (
        <p className="mb-2 text-[12px] font-medium text-danger" data-testid={`profile-country-empty-${country.countryCode}`}>
          Empty — will be discarded. Add a number, or remove this country.
        </p>
      )}
      <div className="space-y-2">
        {country.phones.map((p) => (
          <div key={p.key} className="rounded-sm border border-line/70 p-2">
            <div className="flex items-center gap-2">
              <input
                className="input h-10 flex-1 text-[16px]"
                inputMode="tel"
                placeholder="+1 415 555 0100"
                value={p.number}
                onChange={(e) => onPatchPhone(p.key, { number: e.target.value })}
                data-testid="profile-phone-number"
              />
              <input
                className="input h-10 w-28 text-[16px]"
                placeholder="Label"
                value={p.label}
                onChange={(e) => onPatchPhone(p.key, { label: e.target.value })}
                data-testid="profile-phone-label"
              />
              <button type="button" onClick={() => onRemovePhone(p.key)}
                aria-label="Remove number" className="shrink-0 px-2 text-soft hover:text-danger" data-testid="profile-remove-phone">
                ✕
              </button>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
              <label className="flex items-center gap-1.5 text-[12.5px] text-ink">
                <input type="radio" name="wa-business" checked={p.isWhatsApp}
                  onChange={() => onSetWhatsApp(p.key)} className="h-3.5 w-3.5 accent-gold" data-testid="profile-phone-whatsapp" />
                Business WhatsApp
                {p.isWhatsApp && (
                  <span className="rounded-full bg-gold/15 px-1.5 py-0.5 text-[10.5px] font-semibold text-gold" data-testid="profile-phone-whatsapp-current">
                    current
                  </span>
                )}
              </label>
              <label className="flex items-center gap-1.5 text-[12.5px] text-soft">
                <input type="checkbox" checked={p.isPrimary}
                  onChange={() => onTogglePrimary(p.key)} className="h-3.5 w-3.5 accent-gold" data-testid="profile-phone-primary" />
                Primary
              </label>
            </div>
          </div>
        ))}
      </div>
      <button type="button" onClick={onAddPhone}
        className="mt-2 text-[13px] font-semibold text-gold hover:underline" data-testid={`profile-add-phone-${country.countryCode}`}>
        + Add a number
      </button>
    </div>
  )
}

/** The PERSONAL / agent-facing number — how THIS person reaches our agent on
 *  WhatsApp; distinct from the business WhatsApp buyers see. EDITABLE while
 *  unverified (a plain text box + Save); READ-ONLY once OTP-verified, because a
 *  verified number changes only through the verification flow and a text box
 *  must not shadow it. */
function PersonalNumberSection({
  value, verified, onChange,
}: {
  value: string
  verified: boolean
  onChange: (v: string) => void
}) {
  if (verified) {
    return (
      <Section
        title="Your personal number"
        hint="How you message the DiaEx agent on WhatsApp. This is yours alone — not the number buyers see."
      >
        <p className="text-[14px] text-ink" data-testid="profile-personal-phone">
          <span className="font-medium">{value || '—'}</span>
          <span className="ml-2 rounded-full bg-gold/15 px-1.5 py-0.5 text-[10.5px] font-semibold text-gold">verified</span>
        </p>
        <p className="mt-1 text-[12px] text-soft">
          Verified numbers change through account verification, not here.
        </p>
      </Section>
    )
  }
  return (
    <Section
      title="Your personal number"
      hint="How you message the DiaEx agent on WhatsApp. This is yours alone — not the number buyers see."
    >
      <input
        className="input h-11 w-full text-[16px]"
        inputMode="tel"
        placeholder="+1 415 555 0100"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid="profile-personal-phone-input"
      />
      <p className="mt-1 text-[12px] text-soft" data-testid="profile-personal-phone-hint">
        Not verified yet — verify it from account verification to message our agent on WhatsApp.
      </p>
    </Section>
  )
}

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section className="mt-6">
      <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-soft">{title}</h2>
      {hint && <p className="mt-1 text-[12.5px] text-soft">{hint}</p>}
      <div className="mt-3 space-y-3">{children}</div>
    </section>
  )
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-[12.5px] text-soft">{label}</label>
      {children}
      {hint && <p className="mt-1 text-[12px] text-soft">{hint}</p>}
    </div>
  )
}
